import React, { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Marker } from 'react-map-gl';
import { useSelector } from 'react-redux';

import MapPopup, { IProps as IMapPopupProps } from '@/components/route/search/MapPopup/MapPopup';
import MapMarker, { MapMarkerTypes } from '@/components/route/search/SearchMapMarker/MapMarker';
import { useSearchResultsCtx } from '@/components/route/search/SearchResults/SearchResults.context';
import { useWishlistResultsCtx } from '@/components/wishlists/WishlistDetails/WishlistResults.context';
import { TCurrency } from '@/config/locales';
import { useMapPopupPosition } from '@/hooks/useMapPopupPosition';
import { PriceType } from '@/redux/selectors/listing/page';
import { getFlexibleDateRangeSelected } from '@/redux/selectors/queryParams';
import { IMapMarkerData } from '@/redux/selectors/search/map';
import { trackEscapeListingSelectedEvent } from '@/services/analytics/listings';
import { EListingSource } from '@/services/analytics/listings/types';
import { TFlexibleDateRange } from '@/services/analytics/search/types';
import { formatCurrency } from '@/utility/currency';
import { IBundleTile } from '@/utility/mapSearchResultToTile';
import { preventScrollOnParent } from '@/utility/preventScrollOnParent';

type TMapPopupListingsProp = IMapPopupProps['listings'];

const mapListingToPopup = (
  escape: IBundleTile,
  index: number,
  flexibleDateRangeSelected: TFlexibleDateRange,
  promoSlug?: string,
): TMapPopupListingsProp[number] | null => {
  const handleClick = () => {
    trackEscapeListingSelectedEvent({
      escape,
      eventSource: promoSlug ? EListingSource.PROMO : EListingSource.MAP_PREVIEW,
      listingPlacement: index + 1,
      flexibleDateRange: flexibleDateRangeSelected,
    });
  };

  return {
    id: escape.id,
    title: escape.name,
    price: {
      currentPrice: escape.lowestPrice,
      originalPrice: escape.lowestPrice,
      period: PriceType.DAY,
      discount: 0,
      instantBook: false,
    },
    href: escape.slug,
    image: {
      alt: '',
      src: escape.imageUrls[0] ?? '',
    },
    year: '',
    classText: '',
    length: '',
    rating: undefined,
    infos: [],
    isInstantBook: false,
    isUnavailable: false,
    isSuperhost: false,
    onClick: handleClick,
    isStay: false,
    isCampground: false,
    isFavorite: escape.isFavorite,
    rental: {},
  };
};

type TProps = IMapMarkerData & {
  isPopupVisible: boolean;
  container: Element | null;
  currency: TCurrency;
  onClick?: (id: number) => void;
  promoSlug?: string;
  isOnWishlist?: boolean;
};

const EscapeMapMarker: React.FC<TProps> = ({
  isPopupVisible,
  container,
  currency,
  lat,
  lon,
  escapes,
  onClick,
  promoSlug,
  isOnWishlist,
}) => {
  const markerRef = useRef<HTMLDivElement>(null);
  const intl = useIntl();
  // We use `useState` here because we want to force a rerender when we get the popup ref
  const [popupRef, setPopupRef] = useState<HTMLDivElement | null>(null);
  const popupStyle = useMapPopupPosition({
    container,
    anchor: markerRef.current,
    popup: popupRef,
    isVisible: isPopupVisible,
  });

  const { hoveredId: searchHoveredId, updateMapHoveredId: updateSearchMapHoveredId } =
    useSearchResultsCtx();
  const { hoveredId: wishlistHoveredId, updateMapHoveredId: updateWishlistHoveredId } =
    useWishlistResultsCtx();
  const flexibleDateRangeSelected = useSelector(getFlexibleDateRangeSelected);

  const escapeId = escapes?.[0]?.id || 0;
  const isHovered = isOnWishlist
    ? escapes?.some(r => wishlistHoveredId === r.id)
    : escapes?.some(r => searchHoveredId === r.id);

  const handleOnClick = useCallback<(e: React.MouseEvent<HTMLButtonElement>) => void>(
    e => {
      e.stopPropagation();
      onClick?.(escapeId);
    },
    [escapeId, onClick],
  );

  const handleOnMouseEnter = () => {
    if (isOnWishlist) {
      updateWishlistHoveredId(escapeId);
    } else {
      updateSearchMapHoveredId(escapeId);
    }
  };

  const handleOnMouseLeave = () => {
    if (isOnWishlist) {
      updateWishlistHoveredId(null);
    } else {
      updateSearchMapHoveredId(null);
    }
  };

  // Prevent touch scroll on popup to trigger scroll on body
  const previousBodyOverflow = useRef<string | null>(null);
  const handleTouchStart = useCallback(() => {
    previousBodyOverflow.current = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
  }, []);
  const handleTouchEnd = useCallback(() => {
    if (typeof previousBodyOverflow.current === 'string') {
      window.document.body.style.overflow = previousBodyOverflow.current;
      previousBodyOverflow.current = null;
    }
  }, []);

  const listings = escapes
    ?.map((escape, idx) => mapListingToPopup(escape, idx, flexibleDateRangeSelected, promoSlug))
    .filter(Boolean) as TMapPopupListingsProp;
  const listingsCount = listings?.length || 0;

  if (!listingsCount) {
    return null;
  }

  const isGroup = listingsCount > 1;
  // @ts-expect-error fixable: unchecked index access
  const [{ price }] = listings;

  return (
    <Marker style={{ zIndex: isPopupVisible || isHovered ? 10 : 0 }} latitude={lat} longitude={lon}>
      <div className="relative" ref={markerRef}>
        {isPopupVisible && (
          <MapPopup
            ref={setPopupRef}
            style={popupStyle}
            currency={currency}
            listings={listings}
            onWheelCapture={preventScrollOnParent}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            isOnWishlist={isOnWishlist}
            isEscapeMarker
          />
        )}

        <MapMarker
          type={isGroup ? MapMarkerTypes.GROUP : MapMarkerTypes.SINGLE}
          isActive={isPopupVisible || isHovered}
          onClick={handleOnClick}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
          isOnWishlist={isOnWishlist}>
          {isGroup
            ? listingsCount
            : intl.formatMessage(
                {
                  id: '7rva6N',
                  defaultMessage: 'From {price}',
                },
                {
                  price: formatCurrency({
                    currency,
                    digits: 0,
                    maxDigits: 0,
                    priceInCents: price.currentPrice || price.originalPrice,
                  }),
                },
              )}
        </MapMarker>
      </div>
    </Marker>
  );
};

export default React.memo(EscapeMapMarker);
